import * as React from 'react';
import {useCallback, useEffect, useState} from 'react';
import {useKeyCommands} from '../../../shared-common/hooks/useKeyCommands';
import {usePopper} from 'react-popper';
import ReactDOM from 'react-dom';
import styles from './TooltipProvider.module.css';
import {isNil} from 'lodash-es';
import Helpers from '../../../shared-common/helpers/Helpers';

export interface TooltipProviderProps {
  delayMilliseconds?: number;
}

export const TooltipProvider = (props: TooltipProviderProps) => {
  const [popperElement, setPopperElement] = useState<HTMLElement>(null);
  const [arrowElement, setArrowElement] = useState<HTMLElement>(null);
  const [referenceElement, setReferenceElement] = React.useState<Element>(null);
  const timerHandle = React.useRef<number>(null);

  useEffect(
    () => {
      const observer = new MutationObserver((mutations) => {
        if (!isNil(referenceElement)) {
          let activeTooltip = referenceElement.getAttribute('data-tooltip');
          if (Helpers.isBlank(activeTooltip)) {
            closeTooltip();
          }
        }

        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            closeTooltip();
          }

          if (mutation.type === 'attributes') {
            const newTooltip = (mutation.target as Element).getAttribute('data-tooltip');
            if (!Helpers.isBlank(newTooltip)) {
              showTooltip(mutation.target as Element);
            } else {
              closeTooltip();
            }
          }
        });
      });

      // Observe the entire document
      observer.observe(document.body, {
        childList: true,
        attributes: true,
        attributeFilter: ['data-tooltip'],
        subtree: true
      });

      return () => observer.disconnect();
    },
    [referenceElement]
  );

  useKeyCommands(
    {
      escape: () => {
        closeTooltip();
      }
    },
    popperElement
  );

  const {styles: popperStyles, attributes: popperAttributes}
    = usePopper(
    referenceElement,
    popperElement,
    {
      strategy: 'absolute',
      modifiers: [
        {
          name: 'arrow',
          options: {
            element: arrowElement
          }
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['bottom'],
          }
        },
        {
          name: 'preventOverflow'
        },
        {
          name: 'hide'
        }
      ],
      placement: 'top',
    }
  );

  const showTooltip = useCallback(
    (element: Element) => {
      if (referenceElement !== null) {
        setReferenceElement(element);
      } else {
        timerHandle.current = window.setTimeout(
          () => {
            setReferenceElement(element);
          },
          props.delayMilliseconds
        );
      }
    },
    [referenceElement, setReferenceElement]
  );

  const closeTooltip = useCallback(
    () => {
      window.clearTimeout(timerHandle.current);
      if (isNil(referenceElement)) {
        return;
      }

      referenceElement.removeAttribute('data-tooltip');
      setReferenceElement(null);
    },
    [timerHandle, referenceElement, setReferenceElement]
  );

  if (isNil(referenceElement)) {
    return null;
  }

  const tooltipText = referenceElement.getAttribute('data-tooltip');

  return ReactDOM.createPortal(
    <div
      ref={setPopperElement}
      className={styles.popover}
      style={popperStyles.popper}
      onClick={(event) => {
        event.stopPropagation();
      }}
      {...popperAttributes.popper}
    >
      <span className={styles.text}>{tooltipText}</span>
      <div ref={setArrowElement} className={styles.arrow} style={popperStyles.arrow}/>
    </div>,
    document.body
  );
};